// istanbul ignore file
import { FC, useState, useMemo, memo, useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl';
import { UserAdd } from '@clds/icon';
// FIXME: Extract to external library
// eslint-disable-next-line @nx/enforce-module-boundaries
import { InviteUsersModal, createApiClient } from '@cld/invite-users-modal';
import { API_ORIGIN } from '@cld/session-service';
import { useAnalytics } from '@cld/analytics';
import { messages } from '../../i18n';
import { ADD_NEW_USER_PAGE_URL, MENU_ITEM_ICON_SIZE } from '../../ApplicationWrapper.const';
import { PrimaryMenuItemProps } from '../../components/PrimaryMenu/PrimaryMenu.types';
import { useCustomerIdContext } from '../../context';
import { PrimaryMenuItem } from '../../components/PrimaryMenu/PrimaryMenuItem';
import styled from 'styled-components';
import { useAddUserModal } from '../../components/UpgradePopupModal/usePopupModal';
import { UpgradePopupModal } from '../../components/UpgradePopupModal/UpgradePopupModal';

const API_URL = `${API_ORIGIN}/console/api/v1`;

const IconImage = styled.img<{ size: 'md' | 'sm' }>`
  width: 5px;
  height: 5px;
  vertical-align: top;
`;

const ELLIPSE_ICON = 'https://res.cloudinary.com/prod/image/upload/upgrade-popup-modal/ellipse.png';
export const AddNewUserPrimaryItem: FC<React.PropsWithChildren<PrimaryMenuItemProps & { fetch?: typeof window.fetch }>> = memo(
  ({ fetch = window.fetch, ...props }) => {
    const { formatMessage } = useIntl();
    const { currentCustomer } = useCustomerIdContext();
    const [vacantSeats, setVacantSeats] = useState<number | undefined>(undefined);
    const fetchVacantSeats = useCallback(async () => {
      const commonOptions: RequestInit = {
        credentials: 'include',
        headers: {
          ...(currentCustomer?.externalId ? { 'X-Cld-Customer-External-Id': currentCustomer?.externalId } : {}),
        },
      };

      try {
        // We don't actually need all the users since we only care about total and max users, so we can use page_size=1
        const vacantSeatsResponse = await fetch(`${API_URL}/users/vacant_seats`, commonOptions);
        const data = (await vacantSeatsResponse.json()) as { vacant_seats: number };
        return data.vacant_seats;
      } catch (error) {
        console.error('[AppWrapper] Could not fetch vacant seats', error);
      }
    }, [currentCustomer, fetch]);

    useEffect(() => {
      if (currentCustomer) {
        void fetchVacantSeats().then((data) => {
          if (Number.isInteger(data)) {
            setVacantSeats(data);
          } else {
            console.error('[AppWrapper] Data is undefined or null');
          }
        });
      }
    }, [currentCustomer, fetchVacantSeats, setVacantSeats]);

    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    // FIXME: we should be sure we have the customer data, it's neccessary
    const apiClient = useMemo(
      () =>
        createApiClient({
          apiOrigin: API_ORIGIN,
          customerExternalId: currentCustomer?.externalId ?? '',
        }),
      [currentCustomer]
    );
    const [isUpgradeModalOpen, setUpgradeModalOpen] = useState(false);

    const analytics = useAnalytics('primary menu'); // category
    const onUpgradeModalClose = () => {
      setUpgradeModalOpen(false);
      analytics('new user upgrade modal | close using x button', 'close');
    };
    const displayAddUserModal = useAddUserModal(vacantSeats === 0);

    return (
      <>
        {!displayAddUserModal && (
          <>
            <InviteUsersModal
              customerExternalId={currentCustomer?.externalId ?? ''}
              cloudinaryOrigin={API_ORIGIN}
              isOpen={inviteModalOpen}
              onClose={() => {
                setInviteModalOpen(false);
                void fetchVacantSeats().then((data) => {
                  if (Number.isInteger(data)) {
                    setVacantSeats(data);
                  } else {
                    console.error('[AppWrapper] Data is undefined or null');
                  }
                });
              }}
              apiClient={apiClient}
              logger={{ debug: () => undefined, error: console.error }}
            />

            <PrimaryMenuItem
              type="link"
              analyticsMessage="add new user settings"
              analyticsAction="navigate"
              data-test-specifier="add new user button"
              tooltipTitle={formatMessage(messages.addNewUserMenuTooltip)}
              href={currentCustomer && ADD_NEW_USER_PAGE_URL.replace('{customerId}', currentCustomer.externalIdForUrl)}
              onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                event.preventDefault();
                setInviteModalOpen(true);
              }}
              {...props}
            >
              <UserAdd size={MENU_ITEM_ICON_SIZE} />
            </PrimaryMenuItem>
          </>
        )}
        {displayAddUserModal && (
          <>
            <UpgradePopupModal open={isUpgradeModalOpen} onClose={onUpgradeModalClose} modelPopupKind={'addUserModal'} />

            <PrimaryMenuItem
              type="link"
              analyticsMessage="add new user settings"
              analyticsAction="navigate"
              data-test-specifier="add new user button"
              tooltipTitle={formatMessage(messages.addNewUserMenuTooltip)}
              onClick={() => {
                setUpgradeModalOpen(true);
                analytics('add user upgrade modal', 'open modal');
              }}
              {...props}
            >
              <UserAdd size={MENU_ITEM_ICON_SIZE} />
              <IconImage size={'sm'} src={ELLIPSE_ICON}></IconImage>
            </PrimaryMenuItem>
          </>
        )}
      </>
    );
  }
);

AddNewUserPrimaryItem.displayName = 'AddNewUserPrimaryItem';
